import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { ScrollText, BellRing, Loader2, CheckCircle2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: 'IBM Plex Sans, sans-serif' };
const ROLE = { company_admin: 'Admin', dispatcher: 'Dispatcher', driver: 'Driver', accountant: 'Accountant' };

// Company admins: who on my team still has to accept the current Terms / Privacy.
export const StaffAcceptanceCard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [onlyPending, setOnlyPending] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/legal/company-acceptance`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (user?.role === 'company_admin') load(); }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user?.role !== 'company_admin' || !data) return null;

  const remind = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/legal/company-acceptance/remind`, {}, { withCredentials: true });
      toast.success(r.sent ? `${r.sent} reminder e-mail${r.sent === 1 ? '' : 's'} sent` : 'Everyone pending has already been reminded for this version');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send reminders'); }
    finally { setBusy(false); }
  };

  const rows = onlyPending ? data.staff.filter((s) => s.pending) : data.staff;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 mt-8" data-testid="staff-acceptance-card" style={FONT}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h2 className="text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          <ScrollText className="w-5 h-5 text-[#002FA7]" /> Legal terms acceptance
        </h2>
        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-semibold" data-testid="staff-acceptance-accepted">{data.accepted} accepted</span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-semibold" data-testid="staff-acceptance-pending">{data.pending} pending</span>
        <button type="button" onClick={remind} disabled={busy || data.pending === 0} data-testid="staff-acceptance-remind"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#002FA7] text-white text-xs font-semibold hover:bg-[#00247D] disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Remind pending staff
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Current versions: {data.documents.map((d) => `${d.title} v${d.version}`).join(' · ')}. Staff are prompted to accept on sign-in; CivilX also e-mails anyone still pending a week after a publish.
      </p>
      <label className="inline-flex items-center gap-2 text-xs text-slate-700 mb-2 cursor-pointer">
        <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} className="h-3.5 w-3.5 accent-[#002FA7]" data-testid="staff-acceptance-only-pending" /> Show only pending
      </label>
      {rows.length === 0 ? (
        <p className="text-sm text-emerald-700 flex items-center gap-2 py-3" data-testid="staff-acceptance-all-done"><CheckCircle2 className="w-4 h-4" /> Everyone on your team has accepted the current terms.</p>
      ) : (
        <div className="overflow-x-auto border border-slate-100 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold">Staff member</th><th className="px-3 py-2 font-semibold">Role</th>
                {data.documents.map((d) => <th key={d.slug} className="px-3 py-2 font-semibold">{d.title}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.user_id} className="border-t border-slate-100" data-testid={`staff-acceptance-row-${s.pending ? 'pending' : 'accepted'}`}>
                  <td className="px-3 py-2"><div className="font-semibold text-slate-900">{s.name || '—'}</div><div className="text-slate-500">{s.email}</div></td>
                  <td className="px-3 py-2 text-slate-600">{ROLE[s.role] || s.role}</td>
                  {data.documents.map((d) => {
                    const st = s.documents[d.slug] || {};
                    return (
                      <td key={d.slug} className="px-3 py-2">
                        {st.accepted ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> v{st.accepted_version} · {new Date(st.accepted_at).toLocaleDateString()}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="w-3.5 h-3.5" /> Pending{st.accepted_version ? ` (has v${st.accepted_version})` : ''}{st.reminded_at ? ` · reminded ${new Date(st.reminded_at).toLocaleDateString()}` : ''}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StaffAcceptanceCard;
